/**
 * Scaffold smoke panel (`?smoke=1`): health JSON + hello-room sync + session line. Kept for
 * e2e/smoke.spec.ts; not part of the player-facing shell.
 */
import { Client, type Room } from '@colyseus/sdk';
import { useEffect, useState } from 'react';
import { VERSION } from '@spades/shared';
import { getRuntime } from '../bridge/boot';
import { apiUrl, colyseusEndpoint } from '../net/endpoint';
import { useStore } from '../store/store';

interface Health {
  ok: boolean;
  clientBuilt: boolean;
  rooms: number;
}

interface HelloState {
  msg: string;
}

export function SmokePanel() {
  const [health, setHealth] = useState<Health | { error: string } | null>(null);
  const [roomMsg, setRoomMsg] = useState<string>('connecting…');
  const me = useStore((s) => s.session.me);
  const platform = useStore((s) => s.session.platform);
  const sessionError = useStore((s) => s.session.error);
  const connection = useStore((s) => s.session.connection);
  const layoutMode = useStore((s) => s.presentation.layoutMode);
  const orientation = useStore((s) => s.presentation.orientation);

  useEffect(() => {
    const ac = new AbortController();
    fetch(apiUrl('/api/health'), { signal: ac.signal })
      .then((r) => r.json() as Promise<Health>)
      .then(setHealth)
      .catch((e: unknown) => {
        if (!ac.signal.aborted) setHealth({ error: String(e) });
      });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    let room: Room<unknown, HelloState> | undefined;
    let cancelled = false;
    const client = new Client(colyseusEndpoint());
    client
      .joinOrCreate<HelloState>('hello')
      .then((r) => {
        if (cancelled) {
          void r.leave();
          return;
        }
        room = r;
        setRoomMsg(r.state.msg);
        r.onStateChange((state) => setRoomMsg(state.msg));
      })
      .catch((e: unknown) => {
        if (!cancelled) setRoomMsg(`error: ${String(e)}`);
      });
    return () => {
      cancelled = true;
      void room?.leave();
    };
  }, []);

  const bridgeKind = getRuntime()?.bridge.kind ?? '…';
  const session = me
    ? `${me.name} (${me.kind}) via ${bridgeKind} bridge on ${platform}`
    : sessionError
      ? `no identity: ${sessionError}`
      : 'resolving…';

  return (
    <main className="screen" style={{ fontFamily: 'system-ui, sans-serif', lineHeight: 1.5 }}>
      <div className="screen__inner" style={{ userSelect: 'text' }}>
        <h1>Spades Night</h1>
        <p>shared v{VERSION}</p>
        <p>
          health: <code data-testid="health">{health ? JSON.stringify(health) : 'loading…'}</code>
        </p>
        <p>
          hello room: <code data-testid="room-msg">{roomMsg}</code>
        </p>
        <p>
          session: <code data-testid="session">{session}</code>
        </p>
        <p>
          table: <code data-testid="connection">{connection}</code> · layout <code data-testid="layout">{layoutMode}</code> ·{' '}
          <code data-testid="orientation">{orientation}</code>
        </p>
      </div>
    </main>
  );
}
